/* Token detail panel: a fixed drawer on the right at desktop width, the whole
   screen on phones. Chrome follows terminal.css (black, mono, square, violet
   signal); the history chart and the across-venues table share this file. */

.panel {
  position: fixed;
  inset: 0 0 0 auto;
  z-index: 50;
  display: flex;
  flex-direction: column;
  width: min(600px, 100%);
  overflow-y: auto;
  overscroll-behavior: contain;
  border-left: 1px solid var(--signal);
  background: var(--paper);
  color: var(--ink);
  box-shadow: -16px 0 40px rgba(0, 0, 0, 0.65);
  animation: slide-in 180ms ease-out;
}

.panel:focus-visible {
  outline-offset: -2px;
}

@keyframes slide-in {
  from {
    transform: translateX(24px);
    opacity: 0;
  }

  to {
    transform: none;
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .panel {
    animation: none;
  }
}

@media (max-width: 640px) {
  .panel {
    width: 100%;
    border-left: 0;
    box-shadow: none;
  }
}

/* ---- header ---- */

.head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--line-strong);
  background: var(--head-fill);
}

.titles {
  min-width: 0;
}

.eyebrow {
  margin: 0 0 2px;
  color: var(--signal);
  font-size: 0.66rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.name {
  margin-left: 8px;
  color: var(--ink-muted);
  font-size: 0.78rem;
  font-weight: 400;
}

.halted {
  margin-left: 8px;
  padding: 0 4px;
  background: var(--down);
  color: #000;
  font-size: 0.62rem;
  text-transform: uppercase;
  vertical-align: middle;
}

.close {
  flex: 0 0 auto;
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid var(--line-strong);
  background: #0d0d0d;
  color: var(--ink);
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
}

.close:hover,
.close:focus-visible {
  border-color: var(--signal);
  background: var(--signal-fill);
  color: var(--on-signal);
}

.close kbd {
  margin-left: 6px;
  font: inherit;
  opacity: 0.6;
}

/* ---- body ---- */

.body {
  display: grid;
  gap: 14px;
  padding: 12px 14px 28px;
}

.status {
  margin: 0;
  padding: 14px 10px;
  color: var(--ink-muted);
  font-size: 0.74rem;
}

.error {
  color: var(--down);
}

.meta {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 5px 12px;
  margin: 0;
  font-size: 0.74rem;
}

.meta dt {
  padding-top: 2px;
  color: var(--ink-muted);
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.meta dd {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
}

.chainList {
  display: grid;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.chainList li {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 8px;
}

.chainLabel {
  color: var(--signal);
  font-weight: 700;
}

.muted {
  color: var(--ink-muted);
}

.address {
  word-break: break-all;
}

.inline {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
}

.link {
  color: var(--signal);
  text-decoration: none;
  text-underline-offset: 3px;
}

.link:hover {
  text-decoration: underline;
}

.copy {
  min-height: 22px;
  padding: 0 6px;
  border: 1px solid var(--line-strong);
  background: #0d0d0d;
  color: var(--ink);
  font-size: 0.64rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
}

.copy:hover {
  border-color: var(--signal);
}

.up {
  color: var(--up);
}

.down {
  color: var(--down);
}

/* ---- figures ---- */

.figures {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 150px), 1fr));
  gap: 1px;
  margin: 0;
  padding: 0;
  border: 1px solid var(--line);
  background: var(--line);
  list-style: none;
}

.figure {
  display: grid;
  align-content: start;
  gap: 3px;
  min-width: 0;
  padding: 8px 10px;
  background: var(--paper-raised);
}

.figureLabel {
  color: var(--signal);
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.figureValue {
  overflow: hidden;
  font-size: 1.1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.figureValue[data-tone="up"] {
  color: var(--up);
}

.figureValue[data-tone="down"] {
  color: var(--down);
}

.figureAbsent {
  composes: figureValue;
  color: var(--ink-muted);
  font-size: 0.9rem;
  font-weight: 400;
}

.figureNote {
  color: var(--ink-muted);
  font-size: 0.66rem;
}

/* ---- sections ---- */

.section {
  min-width: 0;
  border: 1px solid var(--line);
  background: var(--paper-raised);
}

.sectionHead {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 12px;
  padding: 6px 10px;
  border-bottom: 1px solid var(--line-strong);
  background: var(--head-fill);
}

.sectionHead h3 {
  margin: 0;
  color: var(--signal);
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.sectionHead span {
  color: var(--ink-muted);
  font-size: 0.68rem;
}

.note {
  margin: 0;
  padding: 6px 10px 8px;
  color: var(--ink-muted);
  font-size: 0.66rem;
}

/* ---- history chart ---- */

.chart {
  min-width: 0;
}

.chartControls {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 6px 12px;
  padding: 8px 10px 0;
}

.keys {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
}

.key {
  min-height: 26px;
  padding: 0 9px;
  border: 1px solid var(--line);
  background: #0d0d0d;
  color: var(--ink-muted);
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
}

.key:hover {
  color: var(--ink);
}

.key[aria-pressed="true"] {
  border-color: var(--signal-fill);
  background: var(--signal-fill);
  color: var(--on-signal);
  font-weight: 700;
}

.readout {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 2px 12px;
  margin: 0;
  padding: 10px 10px 0;
  font-size: 0.76rem;
  font-variant-numeric: tabular-nums;
}

.readoutTime {
  color: var(--ink-muted);
}

.readoutValue {
  font-weight: 700;
}

.stage {
  position: relative;
  min-height: 204px;
  outline-offset: -2px;
  touch-action: pan-y;
}

.svg {
  display: block;
  width: 100%;
  height: 204px;
  overflow: visible;
  cursor: crosshair;
}

.svg text {
  fill: var(--ink-muted);
  font-family: var(--mono);
  font-size: 10px;
}

.svg text.extreme {
  fill: var(--ink);
}

.line {
  fill: none;
  stroke: var(--signal);
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.guide {
  stroke: var(--line-strong);
  stroke-dasharray: 2 4;
}

.tick {
  stroke: var(--line-strong);
}

.crosshair {
  stroke: var(--ink);
  stroke-dasharray: 3 3;
  opacity: 0.7;
}

.dot {
  fill: var(--signal);
  stroke: var(--paper);
  stroke-width: 1.5;
}

.hotDot {
  fill: var(--ink);
  stroke: var(--paper);
  stroke-width: 1.5;
}

.caption {
  margin: 0;
  padding: 6px 10px 10px;
  color: var(--ink-muted);
  font-size: 0.66rem;
}

.chartEmpty {
  margin: 0;
  padding: 36px 14px 24px;
  color: var(--ink-muted);
  font-size: 0.74rem;
  text-align: center;
}

/* ---- across venues ---- */

.scroll {
  max-width: 100%;
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.74rem;
  font-variant-numeric: tabular-nums;
}

.table th {
  padding: 5px 10px;
  border-bottom: 1px solid var(--line);
  color: var(--ink-muted);
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.table td {
  padding: 5px 10px;
  border-bottom: 1px solid #161613;
  vertical-align: top;
  white-space: nowrap;
}

/* The venue cell may wrap its chain list so the figures need less width. */
.table td:first-child {
  min-width: 9em;
  white-space: normal;
}

.table th.num,
.table td.num {
  text-align: right;
}

.table tbody tr:hover td {
  background: var(--signal-soft);
}

.table tbody tr[aria-current="true"] td {
  background: #170d2a;
}

.table tbody tr[data-stale="true"] td {
  color: var(--ink-muted);
}

.table tfoot td {
  border-top: 1px solid var(--line-strong);
  border-bottom: 0;
  background: var(--paper);
  font-weight: 700;
}

.venueButton {
  padding: 0;
  border: 0;
  background: none;
  color: var(--signal);
  font: inherit;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.venueButton:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.sub {
  display: block;
  color: var(--ink-muted);
  font-size: 0.66rem;
}

.stale {
  margin-left: 6px;
  padding: 0 4px;
  background: var(--line-strong);
  color: var(--ink);
  font-size: 0.6rem;
  text-transform: uppercase;
}

.coverage {
  margin-left: 4px;
  color: var(--ink-muted);
  font-size: 0.64rem;
  font-weight: 400;
}

.absent {
  color: var(--ink-muted);
  font-weight: 400;
}

/* Why a table figure is missing, inline in small text. */
.reason {
  color: var(--ink-muted);
  font-size: 0.68rem;
  white-space: nowrap;
}

/* About this fund (DQ-15): cited facts, each with its source link. */
.about {
  display: grid;
  grid-template-columns: minmax(8rem, max-content) 1fr;
  gap: 6px 14px;
  margin: 0;
  padding: 8px 12px 12px;
  font-size: 0.8rem;
}

.about > div {
  display: contents;
}

.about dt {
  color: var(--ink-muted);
}

.about dd {
  margin: 0;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.aboutSource {
  margin-left: 8px;
  color: var(--signal);
  font-size: 0.68rem;
  text-decoration: none;
  white-space: nowrap;
}

.aboutSource:hover {
  text-decoration: underline;
}

@media (max-width: 480px) {
  .about {
    grid-template-columns: 1fr;
    gap: 2px;
  }

  .about dd {
    margin-bottom: 6px;
  }
}
